// Order status constants and utilities

export enum OrderStatus {
	UNSUBMITTED = '1',
	PENDING = '2',
	SENT_TO_ERP = '3',
	APPROVED = '4',
	PARTIALLY_SHIPPED = 'Partially Shipped',
	SHIPPED = '5',
	HELD = '6',
	CANCELLED = '7',
	APPROVAL_REQUIRED = '8',
	OVERRIDE_REJECTED = '9',
	CANCELLATION_REQUESTED = '10',
	OVERRIDE_APPROVED = '11',
	SUBMITTED = '12',
	QUOTE = '13',
	QUOTE_NEEDS_REVIEW = 'Quote - Needs Review',
}

export enum OrderAlertStatus {
	OK = '1',
	WARNING = '2',
	ERROR = '3',
}

export const OrderStatusLabels: Record<OrderStatus, string> = {
	[OrderStatus.UNSUBMITTED]: 'Unsubmitted',
	[OrderStatus.PENDING]: 'Pending',
	[OrderStatus.SENT_TO_ERP]: 'Sent to ERP',
	[OrderStatus.APPROVED]: 'Approved',
	[OrderStatus.PARTIALLY_SHIPPED]: 'Partially Shipped',
	[OrderStatus.SHIPPED]: 'Shipped',
	[OrderStatus.HELD]: 'Held',
	[OrderStatus.CANCELLED]: 'Cancelled',
	[OrderStatus.APPROVAL_REQUIRED]: 'Approval Required',
	[OrderStatus.OVERRIDE_REJECTED]: 'Override Rejected',
	[OrderStatus.CANCELLATION_REQUESTED]: 'Cancellation Requested',
	[OrderStatus.OVERRIDE_APPROVED]: 'Override Approved',
	[OrderStatus.SUBMITTED]: 'Submitted',
	[OrderStatus.QUOTE]: 'Quote',
	[OrderStatus.QUOTE_NEEDS_REVIEW]: 'Quote - Needs Review',
};

/**
 * Badge color configuration for order statuses.
 * These colors provide a consistent visual representation across the application.
 */
export interface OrderStatusBadgeColors {
	bg: string;
	text: string;
}

/**
 * Centralized color mappings for all order statuses.
 * This is the single source of truth for status badge colors throughout the application.
 */
export const OrderStatusColors: Record<string, OrderStatusBadgeColors> = {
	[OrderStatus.UNSUBMITTED]: { bg: '#ca8a04', text: '#ffffff' }, // Yellow-700
	[OrderStatus.PENDING]: { bg: '#f59e0b', text: '#ffffff' }, // Amber-500
	[OrderStatus.SENT_TO_ERP]: { bg: '#3b82f6', text: '#ffffff' }, // Blue-500
	[OrderStatus.APPROVED]: { bg: '#10b981', text: '#ffffff' }, // Emerald-500
	[OrderStatus.PARTIALLY_SHIPPED]: { bg: '#8b5cf6', text: '#ffffff' }, // Purple-500
	[OrderStatus.SHIPPED]: { bg: '#059669', text: '#ffffff' }, // Emerald-600 (darker green for shipped)
	[OrderStatus.HELD]: { bg: '#dc2626', text: '#ffffff' }, // Red-600
	[OrderStatus.CANCELLED]: { bg: '#ef4444', text: '#ffffff' }, // Red-500
	[OrderStatus.APPROVAL_REQUIRED]: { bg: '#f97316', text: '#ffffff' }, // Orange-500
	[OrderStatus.OVERRIDE_REJECTED]: { bg: '#ef4444', text: '#ffffff' }, // Red-500
	[OrderStatus.CANCELLATION_REQUESTED]: { bg: '#f59e0b', text: '#ffffff' }, // Amber-500
	[OrderStatus.OVERRIDE_APPROVED]: { bg: '#10b981', text: '#ffffff' }, // Emerald-500
	[OrderStatus.SUBMITTED]: { bg: '#10b981', text: '#ffffff' }, // Emerald-500
	[OrderStatus.QUOTE]: { bg: '#3b82f6', text: '#ffffff' }, // Blue-500
	[OrderStatus.QUOTE_NEEDS_REVIEW]: { bg: '#f59e0b', text: '#ffffff' }, // Amber-500
};

/**
 * Default colors used when a status is not recognized.
 */
export const DEFAULT_ORDER_STATUS_COLORS: OrderStatusBadgeColors = {
	bg: '#6b7280', // Gray-500
	text: '#ffffff',
};

/**
 * Gets the badge colors for a given order status.
 * Returns the centralized color configuration for consistent styling across the application.
 *
 * @param status - The status code from the database
 * @returns Badge colors (background and text) for the status
 */
export function getOrderStatusBadgeColors(
	status?: string,
): OrderStatusBadgeColors {
	if (status == null) {
		return DEFAULT_ORDER_STATUS_COLORS;
	}

	return OrderStatusColors[status] ?? DEFAULT_ORDER_STATUS_COLORS;
}

/**
 * Maps an order status code to its human-readable label
 * @param status - The status code from the database
 * @returns The human-readable status label
 */
export function getOrderStatusLabel(status?: string): string {
	if (status == null || status === '') {
		return 'Unknown';
	}

	const orderStatus = status as OrderStatus;

	return OrderStatusLabels[orderStatus] ?? status;
}

/**
 * Gets the color variant for a given order status
 * @param status - The status code from the database
 * @returns Color variant for styling
 * @deprecated Use getOrderStatusBadgeColors() for consistent badge colors across the app
 */
export function getOrderStatusColor(
	status?: string,
): 'primary' | 'secondary' | 'warning' | 'danger' | 'info' | 'success' {
	if (status == null) {
		return 'secondary';
	}

	switch (status as OrderStatus) {
		case OrderStatus.UNSUBMITTED:
			return 'secondary';
		case OrderStatus.PENDING:
			return 'warning';
		case OrderStatus.SENT_TO_ERP:
			return 'info';
		case OrderStatus.APPROVED:
			return 'primary';
		case OrderStatus.PARTIALLY_SHIPPED:
			return 'warning';
		case OrderStatus.SHIPPED:
			return 'success';
		case OrderStatus.HELD:
			return 'warning';
		case OrderStatus.CANCELLED:
			return 'danger';
		case OrderStatus.APPROVAL_REQUIRED:
			return 'warning';
		case OrderStatus.OVERRIDE_REJECTED:
			return 'danger';
		case OrderStatus.CANCELLATION_REQUESTED:
			return 'warning';
		case OrderStatus.OVERRIDE_APPROVED:
			return 'primary';
		case OrderStatus.SUBMITTED:
			return 'primary';
		case OrderStatus.QUOTE:
			return 'info';
		case OrderStatus.QUOTE_NEEDS_REVIEW:
			return 'warning';
		default:
			return 'secondary';
	}
}

/**
 * Checks if an order status is considered active (not cancelled or fully completed)
 * @param status - The status code from the database
 * @returns True if the order is active
 */
export function isOrderActive(status?: string): boolean {
	if (status == null) {
		return false;
	}

	return (
		status === OrderStatus.UNSUBMITTED ||
		status === OrderStatus.PENDING ||
		status === OrderStatus.SENT_TO_ERP ||
		status === OrderStatus.APPROVED ||
		status === OrderStatus.PARTIALLY_SHIPPED ||
		status === OrderStatus.HELD ||
		status === OrderStatus.APPROVAL_REQUIRED ||
		status === OrderStatus.OVERRIDE_APPROVED ||
		status === OrderStatus.SUBMITTED
	);
}

/**
 * Checks if an order is completed (fully shipped)
 * @param status - The status code from the database
 * @returns True if the order is completed
 */
export function isOrderCompleted(status?: string): boolean {
	return status === OrderStatus.SHIPPED;
}

/**
 * Checks if an order is cancelled or rejected
 * @param status - The status code from the database
 * @returns True if the order is cancelled or rejected
 */
export function isOrderCancelled(status?: string): boolean {
	return (
		status === OrderStatus.CANCELLED ||
		status === OrderStatus.OVERRIDE_REJECTED ||
		status === OrderStatus.CANCELLATION_REQUESTED
	);
}

/**
 * Checks if an order is a quote
 * @param status - The status code from the database
 * @returns True if the order is a quote
 */
export function isOrderQuote(status?: string): boolean {
	return (
		status === OrderStatus.QUOTE || status === OrderStatus.QUOTE_NEEDS_REVIEW
	);
}

/**
 * Gets all available order statuses for dropdowns/selects
 * @returns Array of status options with value and label
 */
export function getOrderStatusOptions(): Array<{
	value: OrderStatus;
	label: string;
}> {
	return Object.entries(OrderStatusLabels).map(([value, label]) => ({
		value: value as OrderStatus,
		label,
	}));
}
